import React, { useEffect, useState } from 'react';

// Redux
import { useDispatch } from 'react-redux';

// Actions
import { deleteCampaign } from '~/redux/actions/campaign-actions';
import { failure, success } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { DEFAULT_PAGE_SIZES } from '~/utils/constants';
import { stringLength } from '~/utils/helperFunctions';
import { dateFormat } from '~/utils/date-format';

// Hooks
import { useNavigate } from 'react-router-dom';
import useScreenWidth from '~/hooks/useScreenWidth';

// Mui Components
import DataGrid from '~/components/General/OlcDataGrid';
import OlcActionMenu from '~/components/General/OlcActionMenu';
import OlcTablePagination from '~/components/General/OlcTablePagination';
import { Box, Typography, MenuItem, IconButton, Stack, Tooltip } from '@mui/material';

// assets
import { MoreVert as MoreVertIcon } from '@mui/icons-material';
import {
  DeleteCIcon,
  NoContactCIcon,
  DeleteContacts2CIcon,
  ContactDetailsDeleteCIcon,
  TrashTemplateCIcon,
  DuplicateTemplateCIcon,
} from '~/assets/images';

// Components
import ActionsModal from '../ActionsModal';
import DuplicateCampaignModal from '../DuplicateModal';

// Styles
import './styles.scss';
import ConfirmModal from '~/components/General/ConfirmModal';

const classes = {
  root: 'campaignsTableGrid',
  main: 'tableGridMain',
  columnHeaders: 'tableGridHeaderWrapper',
  columnHeader: 'tableGridColumnHeader',
  columnHeaderTitle: 'tableGridColumnHeaderTitle',
  virtualScrollerContent: 'tableGridVirtualScrollerContent',
  virtualScrollerRenderZone: 'tableGridVirtualScrollerRenderZone',
  cell: 'tableGridCell',
  row: 'tableGridRow',
  columnHeaderTitleContainer: 'tableGridColumnHeaderTitleContainer',
};

const CampaignsTable = (props) => {
  const {
    campaigns,
    pagination,
    setPagination,
    anchorElMenu,
    setAnchorElMenu,
    setSelectedCampaign,
    rowsCount,
    perPage,
    isLoading,
    from,
    to,
    currentPage,
    setSearch,
    getCampaigns,
    handleChangePage,
    totalCampaignsInDB,
    searchApplied,
  } = props;
  const [activeRow, setActiveRow] = useState(null);
  const [showNoRows, setShowNoRows] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState({
    open: false,
    campaignId: null,
  });
  const [openConfirmModal, setOpenConfirmModal] = useState({
    type: '',
    open: false,
    message: '',
    handleOk: () => {},
    params: null,
    loading: false,
    header: '',
    successButtonName: '',
    icon: '',
    svg: '',
    className: '',
    modalName: '',
    handleClose: () => {},
  });
  const [openDuplicateModal, setOpenDuplicateModal] = useState(false);
  const [selectedCampaignForDuplicate, setSelectedCampaignForDuplicate] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const pageSizes = DEFAULT_PAGE_SIZES;

  const handleMenuOpen = (event, campaign) => {
    setAnchorElMenu(event.currentTarget);
    setSelectedCampaign(campaign);
    setActiveRow(campaign.campaignId);
  };

  const handleMenuClose = () => {
    setAnchorElMenu(null);
    setSelectedCampaign(null);
    setActiveRow(null);
  };

  const handleRowClick = (params, event) => {
    // Prevent row click when clicking on the menu button
    if (event.target.closest('button') || event.target.closest('.MuiIconButton-root')) {
      return;
    }
    handleEdit(params.row.campaignId);
  };

  const handleCloseDialog = () => {
    setOpenConfirmModal((prev) => ({
      ...prev,
      open: false,
      loading: false,
      campaignId: null,
    }));
  };

  const handleEdit = (campaignId) => {
    if (campaignId) {
      navigate(`/campaigns/edit/${campaignId}`);
    }
    handleMenuClose();
  };

  const handleDuplicate = (campaignId) => {
    const campaign = campaigns.find((camp) => camp.campaignId === campaignId);
    if (!campaign) {
      dispatch(failure(MESSAGES.GENERAL_ERROR));
      return;
    }
    setSelectedCampaignForDuplicate(campaign);
    setOpenDuplicateModal(true);
    handleMenuClose();
  };

  const handleDuplicateSuccess = () => {
    getCampaigns();
  };

  const deleteExistingCategory = async (campaignId) => {
    try {
      setOpenConfirmModal((prev) => ({
        ...prev,
        loading: true,
      }));
      const response = await dispatch(deleteCampaign(campaignId));
      dispatch(success(response.data.message));
      getCampaigns();
      handleCloseDialog();
      setSearch('');
    } catch (error) {
      dispatch(failure(error?.data?.message || MESSAGES.GENERAL_ERROR));
    } finally {
      setTimeout(() => {
        setOpenConfirmModal((prev) => ({
          ...prev,
          loading: false,
        }));
      }, 1000);
    }
  };

  const handleDelete = (campaignId) => {
    handleMenuClose();
    setOpenConfirmModal({
      type: 'delete',
      open: true,
      header: 'Delete Campaign',
      message:
        'Are you sure you want to delete this Campaign? It will remain associated with existing orders but won’t be available for new orders.',
      successButtonName: 'Yes',
      icon: ContactDetailsDeleteCIcon,
      className: 'deleteCampaignModal',
      campaignId: campaignId,
      handleOk: () => {
        deleteExistingCategory(campaignId);
      },
      handleClose: handleCloseDialog,
    });
  };

  // DataGrid columns configuration
  const columns = [
    // {
    //   field: 'campaignId',
    //   headerName: 'ID',
    //   width: 120,
    //   sortable: false,
    //   renderCell: (params) =>
    //     <Box className="campaignValWrapper">
    //       <Typography>{params.row.campaignId ? params.row.campaignId : '-'}</Typography>
    //     </Box>
    // },
    {
      field: 'name',
      headerName: 'Campaign Name',
      minWidth: 180,
      width: 180,
      flex: useScreenWidth(1200) ? 1 : 0,
      sortable: false,
      renderCell: (params) =>
        stringLength(`${params.row.name}`, 32) ? (
          <Tooltip title={params.row.name}>
            <Box className="campaignValWrapper">
              <Typography>{params.row.name ? params.row.name : '-'}</Typography>
            </Box>
          </Tooltip>
        ) : (
          <Box className="campaignValWrapper">
            <Typography>{params.row.name ? params.row.name : '-'}</Typography>
          </Box>
        ),
    },
    {
      field: 'steps',
      headerName: 'Mailer Actions',
      width: 180,
      flex: useScreenWidth(1200) ? 1 : 0,
      // headerAlign: 'center',
      // align: 'center',
      sortable: false,
      renderCell: (params) => (
        <Box className="campaignValWrapper">
          <Typography>{params?.row?.templates.length || '-'}</Typography>
        </Box>
      ),
    },
    {
      field: 'usedInOrders',
      headerName: 'Used in Orders',
      width: 180,
      flex: useScreenWidth(1200) ? 1 : 0,
      sortable: false,
      renderCell: (params) => (
        <Box className="campaignValWrapper">
          <Typography>{params?.row?.noOfOrders || '-'}</Typography>
        </Box>
      ),
    },

    {
      field: 'updatedAt',
      headerName: 'Last Updated Date',
      width: 220,
      flex: useScreenWidth(1200) ? 1 : 0,
      sortable: false,
      renderCell: (params) => (
        <Box className="campaignValWrapper">
          <Typography>{dateFormat(params.row.updatedAt)}</Typography>
        </Box>
      ),
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      width: 300,
      flex: useScreenWidth(1200) ? 1 : 0,
      sortable: false,
      renderCell: (params) => (
        <Box className="campaignValWrapper">
          <Typography>{params?.row?.creator?.fullName || '-'}</Typography>
        </Box>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created Date',
      width: 220,
      flex: useScreenWidth(1200) ? 1 : 0,
      sortable: false,
      renderCell: (params) => (
        <Box className="campaignValWrapper">
          <Typography>{dateFormat(params.row.createdAt)}</Typography>
        </Box>
      ),
    },
    {
      field: ' ',
      headerName: '',
      width: 50,
      sortable: false,
      renderCell: (params) => (
        <Box className="campaignValWrapper">
          <IconButton
            aria-label={`Open actions for campaign ${params.row.name}`}
            id={`campaign-actions-${params.row.campaignId}`}
            aria-controls={
              activeRow === params.row.campaignId ? 'campaign-actions-menu' : undefined
            }
            aria-expanded={activeRow === params.row.campaignId ? 'true' : undefined}
            aria-haspopup="true"
            onClick={(e) => handleMenuOpen(e, params.row)}
            size="small"
            sx={{
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <MoreVertIcon
              sx={{
                // color: activeRow === params.row.campaignId ? '#ed5c2f' : 'inherit',
                opacity: activeRow === params.row.campaignId ? 0.8 : 0.4,
                '&:hover': {
                  opacity: 0.8,
                },
              }}
            />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Add delay for showing no rows overlay
  useEffect(() => {
    let timeoutId;
    if (!isLoading && campaigns.length === 0) {
      // Show "No Results Found" after a delay when not loading and no data
      timeoutId = setTimeout(() => {
        setShowNoRows(true);
      }, 300); // 300ms delay
    } else {
      // Hide immediately when loading or when we have data
      setShowNoRows(false);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading, campaigns.length]);

  return (
    <>
      {/* Campaigns DataGrid */}
      <div
        style={{
          width: '100%',
        }}
        className={`campaignsTableWrapper olcPrimaryListTable ${
          campaigns.length === 0 ? 'noResultsTable' : ''
        }`}
      >
        <DataGrid
          rows={campaigns}
          columns={columns}
          shrinkColumnsToFit
          loading={isLoading}
          pageSizeOptions={[20, 50, 100]}
          rowCount={rowsCount}
          paginationMode="server"
          onRowClick={handleRowClick}
          hideFooterSelectedRowCount
          hideFooterPagination
          rowSelection={false}
          columnHeaderHeight={47}
          rowHeight={59}
          getRowId={(row) => row?.campaignId}
          disableColumnMenu={true}
          classes={{
            ...classes,
            virtualScroller: `tableGridVirtualScroller ${campaigns.length === 0 ? 'noResults' : ''}`,
          }}
          slots={{
            noRowsOverlay: () =>
              showNoRows ? (
                <Typography className="noResultsText">No Results Found</Typography>
              ) : null,
            noResultsOverlay: () =>
              showNoRows ? (
                <Typography className="noResultsText">No Results Found</Typography>
              ) : null,
          }}
        />
        {(isLoading || (searchApplied ? rowsCount : totalCampaignsInDB || rowsCount) > 0) && (
          <OlcTablePagination
            count={Math.max(1, Math.ceil(rowsCount / perPage))}
            page={currentPage || 1}
            pageSize={perPage}
            pageSizes={pageSizes}
            onPageChange={handleChangePage}
            onPageSizeChange={(event) =>
              setPagination({
                page: 1,
                pageSize: event.target.value,
              })
            }
            from={from}
            to={to}
            total={rowsCount}
            loading={isLoading}
          />
        )}
      </div>

      {/* Actions Menu */}
      <OlcActionMenu
        id="campaign-actions-menu"
        MenuListProps={{
          'aria-labelledby': activeRow ? `campaign-actions-${activeRow}` : undefined,
        }}
        anchorEl={anchorElMenu}
        open={Boolean(anchorElMenu)}
        onClose={handleMenuClose}
        transformOrigin={{
          horizontal: 'right',
          vertical: 'top',
        }}
        anchorOrigin={{
          horizontal: 'right',
          vertical: 'bottom',
        }}
      >
        <MenuItem onClick={() => handleDuplicate(activeRow)}>
          <img src={DuplicateTemplateCIcon} alt="Duplicate" height={14} width={14} />
          {MESSAGES.COMPAIGNS.ACTIONS.TABLE.DUPLICATE}
        </MenuItem>
        <MenuItem onClick={() => handleDelete(activeRow)}>
          <img src={TrashTemplateCIcon} alt="Delete" height={16} />
          {MESSAGES.COMPAIGNS.ACTIONS.TABLE.DELETE}
        </MenuItem>
      </OlcActionMenu>

      {/* Actions Modal
      <ActionsModal
        openModal={openDeleteModal.open}
        object={openDeleteModal}
        handleCloseModal={() =>
          setOpenDeleteModal({
            open: false,
            campaignId: null,
          })
        }
        campaignId={openDeleteModal.campaignId}
        pagination={pagination}
        setSearch={setSearch}
        getCampaigns={getCampaigns}
      /> */}

      <ConfirmModal openConfirmModal={openConfirmModal} />

      <DuplicateCampaignModal
        open={openDuplicateModal}
        handleClose={setOpenDuplicateModal}
        selectedRecord={selectedCampaignForDuplicate}
        onSuccess={handleDuplicateSuccess}
      />
    </>
  );
};

export default CampaignsTable;
